<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useAlert } from 'dashboard/composables';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import AssistantSetup from './AssistantSetup.vue';
import AssistantKnowledge from './AssistantKnowledge.vue';
import AssistantTools from './AssistantTools.vue';
import AssistantPlayground from './AssistantPlayground.vue';

// One assistant: setup, knowledge, tools and a playground to try it
const { t } = useLocoI18n();
const route = useRoute();
const router = useRouter();

const TABS = ['setup', 'knowledge', 'tools', 'playground'];
const assistant = ref(null);
const tab = ref('setup');
const deleteDialogRef = ref(null);
const isDeleting = ref(false);

const tabs = computed(() => [
  { key: 'setup', label: t('LOCO.AI.TABS.SETUP') },
  {
    key: 'knowledge',
    label: t('LOCO.AI.TABS.KNOWLEDGE'),
    count: assistant.value?.sources_count,
  },
  {
    key: 'tools',
    label: t('LOCO.AI.TABS.TOOLS'),
    count: assistant.value?.tools_count,
  },
  { key: 'playground', label: t('LOCO.AI.TABS.PLAYGROUND') },
]);

const backToList = () =>
  router.push({
    name: 'loco_ai_assistants',
    params: { accountId: route.params.accountId },
  });

const load = async () => {
  const { data } = await AiAPI.assistant(route.params.assistantId);
  assistant.value = data;
};

const toggleActive = async active => {
  try {
    const { data } = await AiAPI.updateAssistant(assistant.value.id, {
      active,
    });
    assistant.value = data;
    useAlert(
      active
        ? t('LOCO.AI.ASSISTANT.TURNED_ON')
        : t('LOCO.AI.ASSISTANT.TURNED_OFF')
    );
  } catch (error) {
    assistant.value = { ...assistant.value, active: !active };
    useAlert(error.response?.data?.error || error.message);
  }
};

const remove = async () => {
  isDeleting.value = true;
  try {
    await AiAPI.deleteAssistant(assistant.value.id);
    deleteDialogRef.value.close();
    backToList();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isDeleting.value = false;
  }
};

// Counts in the tabs follow what the tabs change
const updateCounts = counts => {
  assistant.value = { ...assistant.value, ...counts };
};

watch(() => route.params.assistantId, load, { immediate: true });
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div
      v-if="assistant"
      class="max-w-4xl mx-auto px-6 py-8 flex flex-col gap-6"
    >
      <header class="flex flex-wrap items-start gap-4">
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <button
            type="button"
            class="self-start flex items-center gap-1 text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="backToList"
          >
            <span class="i-lucide-arrow-left size-3" />
            {{ t('LOCO.AI.ASSISTANTS.TITLE') }}
          </button>
          <h1 class="text-heading-1 text-n-slate-12 truncate">
            {{ assistant.name }}
          </h1>
          <p v-if="assistant.description" class="text-sm text-n-slate-11">
            {{ assistant.description }}
          </p>
        </div>
        <div class="flex items-center gap-4">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <Switch
              :model-value="assistant.active"
              @update:model-value="toggleActive"
            />
            {{
              assistant.active
                ? t('LOCO.AI.ASSISTANTS.ACTIVE')
                : t('LOCO.AI.ASSISTANTS.OFF')
            }}
          </label>
          <Button
            sm
            faded
            ruby
            icon="i-lucide-trash-2"
            :title="t('LOCO.AI.ASSISTANT.DELETE')"
            @click="deleteDialogRef.open()"
          />
        </div>
      </header>

      <TabBar
        :tabs="tabs"
        :initial-active-tab="TABS.indexOf(tab)"
        @tab-changed="selected => (tab = selected.key)"
      />

      <AssistantSetup
        v-if="tab === 'setup'"
        :assistant="assistant"
        @saved="data => (assistant = data)"
      />
      <AssistantKnowledge
        v-else-if="tab === 'knowledge'"
        :assistant="assistant"
        @counts="updateCounts"
        @updated="data => (assistant = data)"
      />
      <AssistantTools
        v-else-if="tab === 'tools'"
        :assistant="assistant"
        @count="count => updateCounts({ tools_count: count })"
      />
      <AssistantPlayground v-else :assistant="assistant" />
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('LOCO.AI.ASSISTANT.DELETE_TITLE', { name: assistant?.name })"
      :description="t('LOCO.AI.ASSISTANT.DELETE_DESC')"
      :confirm-button-label="t('LOCO.AI.ASSISTANT.DELETE')"
      :is-loading="isDeleting"
      @confirm="remove"
    />
  </div>
</template>
